import { Box, Flex, HStack, Stack, Text, Tooltip } from '@chakra-ui/react'
import { useModalAction } from '@components/common/modal/modal.context'
import LocationIconFilled from '@components/icons/location-icon-filled'
import ChakraNextImage from '@components/ui/chakra-next-image'
import { useStore } from '@contexts/store'
import { useSWRGetShopLocations } from '@framework/get-shop-locations'
import { useGetShopPaymentMethods } from '@framework/get-shop-partners'
import { getSocialIcon } from '@helper/globalHelpers'
import { useAnalyticsEvents } from '@hooks/use-analytics-events'
import { DisplayUploadCareAsset } from '@hooks/useUploadCare'
import { ROUTES } from '@utils/routes'
import Link from 'next/link'
import { useMemo } from 'react'
import { BsFillTelephoneFill } from 'react-icons/bs'

const SereneFooter = () => {
  const { storeData: shop } = useStore()
  const {
    logo_uuid,
    name: siteName,
    bio,
    socials: socialsList,
    config,
  } = shop ?? {}
  const { methods: paymentMethods } = useGetShopPaymentMethods(shop?._id)

  const { storefront } = config ?? {}
  const showNameWithLogo = (storefront?.showNameWithLogo ?? true) || !logo_uuid

  const showStoreDetails = storefront?.showStoreDetails

  const { locations } = useSWRGetShopLocations(
    shop?._id,
    'type=warehouse-and-pickupcenter,warehouse',
  )
  const { trackContactUsClick } = useAnalyticsEvents()
  const { openModal } = useModalAction()

  const socials = useMemo(
    () =>
      socialsList?.filter(
        (x: any) => !x?.category || x?.category === 'social',
      ) ?? [],
    [socialsList],
  )

  const storeAddress =
    locations && 'data' in locations
      ? locations?.data?.[0]?.geoLocation?.fullAddress
      : ''

  const footerMenu = [
    {
      text: 'About',
      link: ROUTES.ABOUT_US,
    },
    {
      text: 'Contact',
      link: ROUTES.CONTACT_US,
      extraAction: trackContactUsClick,
    },
    {
      text: 'Find my order',
      action: () => openModal('FIND_MY_ORDER'),
    },
    {
      text: 'Policies',
      link: ROUTES.STORE_POLICIES,
    },
  ]

  return (
    <Flex
      direction="column"
      align="center"
      px={{ base: '5', lg: '12' }}
      pb="10"
    >
      {logo_uuid && (
        <DisplayUploadCareAsset
          src={logo_uuid}
          dimensions={{
            width: 150,
            height: 150,
          }}
          alt={siteName ?? 'logo'}
          options={{ resize: 'x720' }}
        />
      )}
      {showNameWithLogo ? (
        <Text
          color="#0A142F"
          fontWeight="700"
          fontSize={{ base: '20px', lg: '36px' }}
          mt="5"
        >
          {shop?.name}
        </Text>
      ) : null}

      <Text fontSize="14px" opacity="0.8" mt="5">
        {bio}
      </Text>

      {storeAddress ? (
        <HStack mt="5">
          <LocationIconFilled boxSize={'24px'} />
          <Text fontSize="14px" color="#0A142F" textAlign="center">
            {storeAddress}
          </Text>
        </HStack>
      ) : null}

      {showStoreDetails ? (
        <HStack mt="5">
          <BsFillTelephoneFill size={24} />
          <Text fontSize="14px" color="#0A142F" textAlign="center">
            {shop?.whatsapp_number || shop?.telephone}
          </Text>
        </HStack>
      ) : null}

      <Stack
        spacing={'3.5'}
        direction={{ base: 'column', md: 'row' }}
        as="ul"
        mt="5"
        align="center"
        listStyleType="none"
      >
        {footerMenu.map(({ text, action, link, extraAction }) => (
          <Box
            as="li"
            bg="#F7F9FB"
            py="2.5"
            px="5"
            borderBottom="1px solid #ebebeb"
            key={text}
            rounded="full"
            textTransform="capitalize"
            fontSize="14px"
          >
            {link ? (
              <Link prefetch={false} href={link} onClick={extraAction}>
                {text}
              </Link>
            ) : action ? (
              <Text
                as="span"
                cursor="pointer"
                onClick={() => {
                  action()
                }}
              >
                {text}
              </Text>
            ) : null}
          </Box>
        ))}
      </Stack>

      <Stack
        w="full"
        direction={{ base: 'column', lg: 'row' }}
        align="center"
        spacing="5"
        mt="5"
        pt="6"
        borderTop="2px solid"
        borderColor="gray.100"
        position="relative"
        justify={{ lg: 'space-between' }}
      >
        {storefront?.showSocialLinks && socials && socials?.length >= 1 && (
          <HStack
            as="ul"
            listStyleType="none"
            spacing="3.5"
            align="center"
            justify="center"
            flexWrap="wrap"
          >
            {socials.map(
              (
                social: {
                  name: string
                  url: string
                  slug: string
                  [x: string | number | symbol]: unknown
                },
                i: number,
              ) => {
                let url = social?.url
                if (!url) return null
                if (!url.includes('http')) {
                  url = `https://${url}`
                }
                return (
                  <Box
                    as="li"
                    border="1px solid"
                    borderColor="gray.100"
                    p="2"
                    borderRadius="full"
                    key={social.slug ?? i}
                  >
                    <a
                      href={url ?? '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ChakraNextImage
                        src={getSocialIcon(
                          social.slug ??
                            social?.name?.split(' ')[0]?.toLowerCase(),
                        )}
                        alt={social.name ?? social?.slug ?? 'social'}
                        width={18}
                        height={18}
                      />
                    </a>
                  </Box>
                )
              },
            )}
          </HStack>
        )}
        <Text
          textAlign="center"
          position={{ base: 'static', lg: 'absolute' }}
          left="50%"
          transform={{ lg: 'translateX(-50%)' }}
        >
          &copy; {new Date().getFullYear()} {shop?.name}. All Rights Reserved.
        </Text>
        {paymentMethods?.length > 0 && (
          <Flex
            gap={3}
            mb={{ base: '3.5', lg: '0' }}
            className="flex flex-wrap justify-center items-center space-s-4 sm:space-s-5 lg:space-s-7 -mb-1.5 md:mb-0 mx-auto md:mx-0 pt-3.5 md:pt-0"
          >
            {paymentMethods?.map((integration: any) => (
              <Tooltip key={integration._id} label={integration.name}>
                <Box
                  as="li"
                  className="mb-2 md:mb-0 transition hover:opacity-80 inline-flex"
                  key={`payment-list--key${integration?._id}`}
                >
                  <DisplayUploadCareAsset
                    src={integration?.miniImageId ?? integration?.imageId}
                    dimensions={{ width: 25, height: 25 }}
                    options={{ resize: '100x' }}
                    alt={integration.name}
                  />
                </Box>
              </Tooltip>
            ))}
          </Flex>
        )}
      </Stack>
    </Flex>
  )
}

export default SereneFooter
